/**
 * `<BottomSheet/>` — the shared mobile presentation primitive (#966).
 *
 * A bottom-anchored sheet that slides up from the screen edge — the
 * phone-native replacement for a centered modal. Built on the same Radix
 * dialog foundation as `ui/Dialog` (so it gets focus-trap, escape-to-close,
 * scroll-lock, the portal, and correct ARIA roles for free — no hand-rolled
 * `role="dialog"`), with a grab-handle, safe-area padding, and the
 * `slide-up` transition.
 *
 * This is the reusable backbone for the mobile presentation layer: the
 * member "More" menu today, and — across P2–P5 — the cycle `?surface=`
 * modals, leave/swap confirm cards, pickers, and any other surface that
 * presents modally on a phone. Desktop is unaffected (`md:hidden`): desktop
 * callers keep `ui/Dialog`.
 *
 * **Half-height snap (#1922, R6).** A sheet that is decided against the
 * screen above it — the member's *Request a swap* against their own month,
 * the lead's call-back ladder against the day card — opens with
 * `snap="half"`: at most half the screen, NO scrim, and non-modal (Radix
 * `modal={false}`, outside interactions never close it), so the page above
 * stays visible, scrollable and tappable. A chevron on the grab-handle row
 * toggles half ↔ full; full height restores the scrim and the modal
 * behaviour (focus trap, scroll lock, a tap on the scrim closes). Toggling
 * re-mounts the sheet's content (Radix renders a different content element
 * per mode), so a surface keeps its state ABOVE the sheet — the ladder and
 * the swap form both do. Without `snap` the sheet is exactly what it was:
 * full height, scrim, modal, no chevron.
 */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { cn } from "../../lib/cn";

export type BottomSheetSnap = "full" | "half";

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Visible sheet title. Omit + pass `ariaLabel` for a title-less sheet. */
  title?: ReactNode;
  /** Accessible name when there is no visible `title` (Radix requires one). */
  ariaLabel?: string;
  /** Hide the default close (X) button — e.g. for a confirm sheet with its own actions. */
  hideClose?: boolean;
  /** The height the sheet opens at, and whether it can be toggled. Omitted
   *  = full height, no toggle (the original sheet). `"half"` / `"full"` =
   *  opens there, with a chevron to switch. */
  snap?: BottomSheetSnap | undefined;
  className?: string;
  children: ReactNode;
}

export function BottomSheet({
  open,
  onOpenChange,
  title,
  ariaLabel,
  hideClose = false,
  snap,
  className,
  children,
}: BottomSheetProps) {
  const snappable = snap !== undefined;
  const [current, setCurrent] = useState<BottomSheetSnap>(snap ?? "full");
  // A user toggle replays no slide-up (the sheet is already on screen).
  const [toggled, setToggled] = useState(false);
  // Every opening starts at the caller's snap: reset on close, so the next
  // open's first frame is already right.
  useEffect(() => {
    if (open) return;
    setCurrent(snap ?? "full");
    setToggled(false);
  }, [open, snap]);

  const half = snappable && current === "half";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={!half}>
      <DialogPrimitive.Portal>
        {/* Radix renders the overlay in modal mode only, so the half-height
            sheet has no scrim and no scroll lock. */}
        <DialogPrimitive.Overlay className="motion-scrim fixed inset-0 z-50 bg-black/40 backdrop-blur-sm md:hidden" />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 overflow-y-auto",
            half ? "max-h-[50vh]" : "max-h-[85vh]",
            "rounded-t-2xl border-t border-border bg-bg-elevated pb-safe-bottom shadow-lg",
            // #2172 — slides up on open, back down on close (`motion-sheet`,
            // motion-shell.css); a snap toggle's remount (`data-toggled`)
            // plays nothing.
            "motion-sheet focus:outline-none md:hidden",
            className,
          )}
          data-snap={snappable ? current : undefined}
          data-toggled={toggled ? "" : undefined}
          // No separate description; the sheet content is self-describing.
          aria-describedby={undefined}
          // At half height the page above is meant to be used: a tap or a
          // focus move out there never closes the sheet.
          {...(half ? { onInteractOutside: (event: Event) => event.preventDefault() } : {})}
        >
          {snappable ? (
            // The grab-handle row doubles as the snap toggle.
            <div className="flex justify-center pt-1">
              <button
                type="button"
                onClick={() => {
                  setToggled(true);
                  setCurrent(half ? "full" : "half");
                }}
                className="flex min-h-tap min-w-tap flex-col items-center justify-center gap-0.5 rounded-md text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                aria-label={half ? "Expand to full height" : "Shrink to half height"}
                aria-expanded={!half}
                data-testid="bottom-sheet-snap-toggle"
              >
                <span className="h-1 w-10 rounded-full bg-border" aria-hidden="true" />
                {half ? (
                  <ChevronUp className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            </div>
          ) : (
            /* Grab handle (decorative). */
            <div className="flex justify-center pt-3" aria-hidden="true">
              <div className="h-1 w-10 rounded-full bg-border" />
            </div>
          )}
          {/* Sticky so the Close (X) stays reachable while a long sheet —
              the shortfall ladder at 360px, say — scrolls under it. */}
          <div className="sticky top-0 z-10 flex items-center justify-between bg-bg-elevated px-5 pb-1 pt-2">
            {title ? (
              <DialogPrimitive.Title className="text-sm font-semibold text-fg">
                {title}
              </DialogPrimitive.Title>
            ) : (
              <DialogPrimitive.Title className="sr-only">
                {ariaLabel ?? "Menu"}
              </DialogPrimitive.Title>
            )}
            {hideClose ? null : (
              <DialogPrimitive.Close
                className="flex min-h-tap min-w-tap items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                aria-label="Close"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </DialogPrimitive.Close>
            )}
          </div>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
